import { LocalDBService } from './../../service/local-db.service';
import { AwsUtil } from '../../authentication/aws.service';
import { Component, OnInit, Inject } from '@angular/core';
import {
    WebApiEndpoints,
    GetCurvesRequest,
    YieldCurveOutputData,
    ApplicationConfiguration,
} from '@liquitics/transaction';
import { chunkArray } from '@liquitics/transaction';
import { MAT_DIALOG_DATA } from '@angular/material/dialog';
import { TreeNode } from 'primeng/api';

import * as am4core from '@amcharts/amcharts4/core';
import * as am4charts from '@amcharts/amcharts4/charts';
import am4themes_material from '@amcharts/amcharts4/themes/material';
am4core.useTheme(am4themes_material);

const SCENARIOS = ['BASE',
    'STRESS_SCENARIO_1',
    'STRESS_SCENARIO_2',
    'STRESS_SCENARIO_3',
    'STRESS_SCENARIO_4',
    'STRESS_SCENARIO_5',];


function getScenarioDisplayName(scenario: string, applicationConfiguration: ApplicationConfiguration): string {
    const index = SCENARIOS.indexOf(scenario);
    return applicationConfiguration.scenarioNames[index];
}

export async function getAllCurvesTreeNodes(currencies: string[],
    completedDay: string,
    awsUtil: AwsUtil,
    applicationConfiguration: ApplicationConfiguration): Promise<TreeNode[]> {
    const currencyNodes: TreeNode[] = [];
    const requests: GetCurvesRequest[] = [];
    const requestMapNodes: TreeNode[] = [];
    const MKT_DATA_CURRENCY_BATCH_MOD_DIVISOR = 5;
    const MKT_DATA_SPREAD_BATCH_MOD_DIVISOR = 10;
    const currencyModulusIndicies = Array.from(Array(MKT_DATA_CURRENCY_BATCH_MOD_DIVISOR).keys());
    const spreadModulusIndicies = Array.from(Array(MKT_DATA_SPREAD_BATCH_MOD_DIVISOR).keys());
    for (let c = 0; c < currencies.length; c++) {
        const scenarioNodes: TreeNode[] = [];
        const currencyNode = {
            label: currencies[c],
            children: scenarioNodes,
            selectable: false,
        } as TreeNode;
        currencyNodes.push(currencyNode);
        for (let s = 0; s < SCENARIOS.length; s++) {
            const scenario = SCENARIOS[s];
            const curveNodes: TreeNode[] = [];
            const scenarioNode = {
                label: getScenarioDisplayName(scenario, applicationConfiguration),
                children: curveNodes,
                selectable: false,
            } as TreeNode;
            scenarioNodes.push(scenarioNode);
            currencyModulusIndicies.forEach((currencyModulusIndex: number) => {
                spreadModulusIndicies.forEach((spreadModulusIndex: number) => {
                    requests.push({
                        operatingDate: completedDay,
                        currency: currencies[c],
                        stressScenarioName: scenario,
                        currencyModulusIndex: currencyModulusIndex,
                        spreadModulusIndex: spreadModulusIndex,
                    } as GetCurvesRequest);
                    requestMapNodes.push(scenarioNode);
                });
            });
        }
    }


    const requestChunks: GetCurvesRequest[][] = chunkArray(requests, 10);
    const allYCData: YieldCurveOutputData[][] = await requestChunks.reduce(
        (promiseChain: Promise<YieldCurveOutputData[][]>, requestChunk: GetCurvesRequest[]) => {
            return promiseChain
                .then((allYCDataChunks: YieldCurveOutputData[][]) => {
                    return Promise.all(requestChunk
                        .map((request: GetCurvesRequest) => {
                            return awsUtil.webAPICall<YieldCurveOutputData[]>(WebApiEndpoints.QueryCurves, request);
                        })
                    ).then((requestChunkResults) => {
                        return [...allYCDataChunks, requestChunkResults];
                    })
                })
        }, Promise.resolve([]));

    // const allYCData: YieldCurveOutputData[][] = await Promise.all(requests
    //     .map((request: GetCurvesRequest) => {
    //         return awsUtil.webAPICall<YieldCurveOutputData[]>(WebApiEndpoints.QueryCurves, request);
    //     })
    // );
    allYCData
        .forEach((ycDataBatch: YieldCurveOutputData[], batchIndex: number) => {
            ycDataBatch
                .forEach((ycData: YieldCurveOutputData) => {
                    const curveNodes = requestMapNodes[batchIndex].children;
                    const curveNode = {
                        label: `${ycData.currency}/${getScenarioDisplayName(ycData.stressScenarioName, applicationConfiguration)}/${ycData.classifier}`,
                        data: ycData,
                    } as TreeNode;
                    curveNodes.push(curveNode);
                })

        })

    return currencyNodes;
}


@Component({
    selector: 'app-display-curve',
    templateUrl: './display-curve.component.html',
    styleUrls: ['./display-curve.component.css']
})
export class DisplayCurveComponent implements OnInit {
    // curves: SelectItem[];
    // selectedCurve: string;
    curveNodes: TreeNode[];
    selectedCurveNodes: TreeNode[];
    selectedDisplayDataType = 'rate';

    constructor(@Inject(MAT_DIALOG_DATA) public data: any,
        private localDBService: LocalDBService) {
    }

    ngOnInit() {
        this.localDBService.getRegisteredPromise('AllCurveTreeNodes')
            .then((nodes: TreeNode[]) => {
                this.curveNodes = nodes;
            })
    }


    onCurveSelect(event: any) {
        this.showCurvesChart();
    }

    generateCurveChartData(): any[] {
        const data: any[] = [];
        this.selectedCurveNodes
            .filter((node: TreeNode) => {
                return node.data;
            })
            .forEach((node: TreeNode, nodeIndex: number) => {
                const yc: YieldCurveOutputData = node.data;
                if (nodeIndex === 0) {
                    for (let i = 0; i < yc.points.dates.length; i++) {
                        data.push({
                            date: yc.points.dates[i],
                            rate0: yc.points.rates[i],
                            df0: yc.points.dfs[i],
                        });
                    }
                } else {
                    for (let i = 0; i < yc.points.dates.length; i++) {
                        data[i][`rate${nodeIndex}`] = yc.points.rates[i];
                        data[i][`df${nodeIndex}`] = yc.points.dfs[i];
                    }
                }
            });
        return data;
    }

    onDisplayDataTypeChange(event: any) {
        this.showCurvesChart();
    }

    showCurvesChart() {
        const chart = am4core.create('curvechartdiv', am4charts.XYChart);

        // Add data
        chart.data = this.generateCurveChartData();
        chart.logo.visible = false;

        // Create axes
        let dateAxis = chart.xAxes.push(new am4charts.DateAxis());
        dateAxis.renderer.minGridDistance = 50;
        dateAxis.baseInterval = {
            timeUnit: 'day',
            count: 1,
        };

        let valueAxis = chart.yAxes.push(new am4charts.ValueAxis());

        // Create series
        this.selectedCurveNodes
            .filter((node: TreeNode) => {
                return node.data;
            })
            .forEach((node: TreeNode, nodeIndex: number) => {
                let series = chart.series.push(new am4charts.LineSeries());
                series.dataFields.valueY = `${this.selectedDisplayDataType}${nodeIndex}`;
                series.dataFields.dateX = 'date';
                series.strokeWidth = 2;
                series.minBulletDistance = 10;
                series.tooltipText = `${node.label}: {valueY}`;
                series.tooltip.pointerOrientation = 'vertical';
                series.tooltip.background.cornerRadius = 20;
                series.tooltip.background.fillOpacity = 0.5;
                series.tooltip.label.padding(12, 12, 12, 12);
            });
        chart.dateFormatter.dateFormat = 'MM/dd/yyyy';
        if (this.selectedDisplayDataType === 'rate') {
            chart.numberFormatter.numberFormat = '#.00%';
        }

        // Add scrollbar
        chart.scrollbarX = new am4charts.XYChartScrollbar();

        // Add cursor
        chart.cursor = new am4charts.XYCursor();
        chart.cursor.xAxis = dateAxis;
        chart.cursor.behavior = 'zoomX';



        // if (chart.series.length === 1) {
        //     chart.cursor.snapToSeries = chart.series[0];
        // }
    }
}
